import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, InputAdornment, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { AccountType, OpeningBalances } from '../../../types/finance';
import { formatStatementPaisa, toSignedPaisa } from '../../../utils/paisa';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useAuth } from '../../../context/AuthContext';

const SECTIONS: Array<{ type: AccountType; label: string; total: string }> = [
  { type: 'ASSET', label: 'Assets', total: 'Total assets' },
  { type: 'LIABILITY', label: 'Liabilities', total: 'Total liabilities' },
  { type: 'EQUITY', label: 'Equity', total: 'Other equity' },
];

/** Paisa as an editable figure: blank for zero, two decimals otherwise. */
const asInput = (paisa: number) => (paisa ? (paisa / 100).toFixed(2) : '');

/**
 * What each balance-sheet account held on the go-live day, posted as one
 * entry. Owner's capital is the balancing figure. Fees receivable and
 * advances come from the challans, so they aren't entered here. Saving again
 * replaces the entry, while the go-live month is open.
 */
const OpeningBalancesPanel: React.FC = () => {
  const theme = useTheme();
  const { canWrite } = useModulePermissions();
  const { currency } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<OpeningBalances | null>(null);
  const [date, setDate] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await financeService.getOpeningBalances();
      setData(d);
      setDate(d.date);
      setDraft(Object.fromEntries(d.accounts.map(a => [a._id, asInput(a.balancePaisa)])));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the opening balances.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const editable = canWrite('finance_accounts') && !!data?.editable;

  const view = useMemo(() => {
    if (!data) return null;
    const paisa = new Map(data.accounts.map(a => [a._id, toSignedPaisa(draft[a._id] ?? '') ?? (draft[a._id]?.trim() ? null : 0)]));
    const invalid = new Set(data.accounts.filter(a => paisa.get(a._id) === null).map(a => a._id));
    const value = (id: string) => paisa.get(id) ?? 0;
    // A section's total in its own direction: assets as debits, the rest as credits.
    const totals = Object.fromEntries(SECTIONS.map(s => [s.type, data.accounts
      .filter(a => a.type === s.type)
      .reduce((sum, a) => sum + ((a.normalBalance === 'DEBIT') === (s.type === 'ASSET') ? value(a._id) : -value(a._id)), 0)]));
    const capital = totals.ASSET - totals.LIABILITY - totals.EQUITY;
    const dirty = date !== data.date || data.accounts.some(a => value(a._id) !== a.balancePaisa);
    return { value, invalid, totals, capital, dirty };
  }, [data, draft, date]);

  const save = async () => {
    if (!data || !view) return;
    setBusy(true);
    try {
      const r = await financeService.saveOpeningBalances({
        date,
        balances: data.accounts.filter(a => view.value(a._id) !== 0).map(a => ({ accountId: a._id, balancePaisa: view.value(a._id) })),
      });
      enqueueSnackbar(r ? `Opening balances posted — ${r.entryNo}` : 'Opening balances cleared', { variant: 'success' });
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not save the opening balances.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data || !view) return <Stack spacing={2}><Skeleton height={72} /><Skeleton variant="rounded" height={320} /></Stack>;

  const figure = (paisa: number, weight = 600) => (
    <Typography variant="body2" fontWeight={weight} color={paisa < 0 ? 'error.main' : 'text.primary'} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {formatStatementPaisa(paisa)}
    </Typography>
  );

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small" type="date" label="Go-live date" value={date} disabled={!editable}
            onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 190 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {data.entry ? `Posted as ${data.entry.entryNo}` : 'Not entered yet'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.entry
                ? data.entry.revisions ? `Revised ${data.entry.revisions} time${data.entry.revisions === 1 ? '' : 's'} — each revision reverses the previous entry.` : 'Saving again replaces it.'
                : 'Balances on the day the school started keeping its books here.'}
            </Typography>
          </Box>
          {canWrite('finance_accounts') && (
            <Button variant="contained" onClick={save} disabled={!editable || busy || !view.dirty || view.invalid.size > 0 || !date}>
              {busy ? 'Saving…' : data.entry ? 'Save changes' : 'Post opening balances'}
            </Button>
          )}
        </Stack>
      </Paper>

      {!data.editable && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          The go-live month is closed, so the opening balances are final. Reopen that month to change them.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell sx={{ py: 1.5 }}>Account</TableCell>
                <TableCell align="right" sx={{ width: 220, py: 1.5 }}>Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SECTIONS.map(s => {
                const rows = data.accounts.filter(a => a.type === s.type);
                if (!rows.length) return null;
                return (
                  <React.Fragment key={s.type}>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ pt: 2.5, pb: 1, borderBottom: `1.5px solid ${theme.palette.divider}` }}>
                        <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>{s.label}</Typography>
                      </TableCell>
                    </TableRow>
                    {rows.map(a => (
                      <TableRow key={a._id} hover>
                        <TableCell sx={{ pl: 4 }}>
                          <Typography variant="body2">
                            <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 1 }}>{a.code}</Box>
                            {a.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5 }}>
                          <TextField
                            size="small" value={draft[a._id] ?? ''} placeholder="0.00" disabled={!editable}
                            error={view.invalid.has(a._id)}
                            onChange={e => setDraft(d => ({ ...d, [a._id]: e.target.value }))}
                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
                            InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
                            sx={{ width: 200 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025), '& td': { borderTop: `1px solid ${theme.palette.divider}` } }}>
                      <TableCell><Typography variant="body2" fontWeight={700}>{s.total}</Typography></TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>{figure(view.totals[s.type], 700)}</TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), '& td': { borderTop: `2px solid ${theme.palette.text.primary}`, borderBottom: `3px double ${theme.palette.text.primary}` } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={800}>3100 Owner's capital</Typography>
                  <Typography variant="caption" color="text.secondary">The balancing figure: total assets less liabilities and other equity.</Typography>
                </TableCell>
                <TableCell align="right" sx={{ pr: 3 }}>{figure(view.capital, 800)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Fees receivable and advances aren't entered here — they come from the challans themselves. Enter a negative amount for an account held on its unusual side, such as an overdrawn bank.
      </Typography>
    </Box>
  );
};

export default OpeningBalancesPanel;
